import * as React from 'react'
import styled from 'styled-components'
import { FormikHelpers } from 'formik'

import {
  AsyncStatus,
  EnrollAdditionalAutoTypeSelectProps,
} from '@ally/transmitigator'
import { Box, Button, Label, Space } from '@ally/metronome-ui'
import {
  CardSelectionCard,
  CardSelectionGroup,
  Form,
  Forms,
  useFormsContext,
} from '@ally/forms'
import { LoginHeading } from '@ally/ally-login'

import { useTransmit } from '../../TransmitContext'
import { FindAccountNumberModal, FindVINModal } from '../../Modals'
import { StyledTextBody } from '../Shared'

const HiddenLabel = styled(Label)`
  display: none;
`

const StyledCardSelectionGroup = styled(CardSelectionGroup)`
  ${({ theme: { media } }): string => `
    ${media.up('lg')} {
      max-width: 360px;
    }
  `}
`

interface VerifyAutoTypeSelectForm {
  formId: string
  status: AsyncStatus
}

export const VerifyAutoTypeSelectForm: React.FC<VerifyAutoTypeSelectForm> = ({
  formId,
  status,
}) => {
  const { values } = useFormsContext()
  const { navigateToOleCustomer } = useTransmit()
  const [shouldShowBackButton, setShouldShowBackButton] = React.useState<
    boolean
  >(true)

  React.useEffect(() => {
    // Since you cannot unselect from a CardSelectionGroup we only need to check this case
    if (shouldShowBackButton && values.type.length > 0) {
      setShouldShowBackButton(false)
    }
  }, [values.type])

  return (
    <Form id={formId}>
      <HiddenLabel htmlFor="auto-select-type" id="auto-select-type-label">
        Select which number you&apos;d like to use to verify your identity.
      </HiddenLabel>
      <StyledCardSelectionGroup
        align="stacked"
        ariaLabelledby="auto-select-type-label"
        id="auto-select-type"
        name="type"
        variant="single-select"
      >
        <CardSelectionCard
          cardId="accountNumber"
          data-testid="account-number-card"
          title="Account Number"
        />
        <CardSelectionCard
          cardId="vin"
          data-testid="vin-card"
          title="VIN/Hull ID"
        />
      </StyledCardSelectionGroup>

      <Box mb="md" mt="md">
        <FindAccountNumberModal />
      </Box>

      <Box mb="lg">
        <FindVINModal />
      </Box>

      {shouldShowBackButton ? (
        <Button
          data-testid="back-button"
          text="Back"
          type="button"
          onClick={navigateToOleCustomer}
          variant="primary"
          fullWidthMd
          allytmln="back"
        />
      ) : (
        <Button
          data-testid="continue-button"
          text="Continue"
          type="submit"
          variant="primary"
          loading={status === 'Loading'}
          fullWidthMd
          allytmln={`${values.type}-continue`}
        />
      )}
    </Form>
  )
}

interface VerifyAutoTypeSelectFormValues {
  type: string
}

export const VerifyAutoTypeSelectContent: React.FC<EnrollAdditionalAutoTypeSelectProps> = ({
  onComplete,
  status,
}) => {
  const formId = 'verify-auto-select-type-form'
  const initialValues = { type: '' }
  const handleSubmit = (
    { type }: VerifyAutoTypeSelectFormValues,
    { setSubmitting }: FormikHelpers<VerifyAutoTypeSelectFormValues>,
  ): void => {
    onComplete({
      accountNumber: type === 'accountNumber',
      vin: type === 'vin',
    })

    // Since onComplete does not return a promise, we need to immediately reset the
    // isSubmitting flag in order to be able to resubmit the form in case of error
    setSubmitting(false)
  }

  return (
    <>
      <LoginHeading title="We need to make sure it's you." />

      <Space mb="md">
        <StyledTextBody size="sm" tag="p">
          Confirm your auto account by entering your 12-digit account number
          (which can be found on a recent billing statement) or your VIN/Hull
          ID.
        </StyledTextBody>
      </Space>

      <Forms
        formId={formId}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <VerifyAutoTypeSelectForm formId={formId} status={status} />
      </Forms>
    </>
  )
}

export const VerifyAutoTypeSelect: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'EnrollAutoTypeSelect') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <VerifyAutoTypeSelectContent {...activeScreen.payload} />
}
